import { MAPBOX_TOKEN } from "@/lib/env";
import type { LngLat } from "@/utils/polyline";
import type { TravelMode } from "@/api/routing";

const MODE_PROFILE: Record<TravelMode, string> = {
  // driving-traffic supports max 10 coords per matrix request; plain driving allows 25
  driving: "driving",
  walking: "walking",
  biking: "cycling",
};

/** Module-level cache so multiple components and re-mounts share results.
 *  Key encodes the rounded origin (~110m), rounded destination (~11m), and
 *  travel mode. TTL keeps stale traffic estimates from sticking around. */
const TTL_MS = 5 * 60 * 1000; // 5 minutes
const MAX_ENTRIES = 2000;
type CacheEntry = { durationS: number; distanceM: number; ts: number };
const cache = new Map<string, CacheEntry>();

function cacheKey(origin: LngLat, dest: LngLat, mode: TravelMode): string {
  // Origin rounded coarser (the user is moving) than destination (fixed POI).
  const o = `${origin[0].toFixed(3)},${origin[1].toFixed(3)}`;
  const d = `${dest[0].toFixed(4)},${dest[1].toFixed(4)}`;
  return `${mode}|${o}|${d}`;
}

function cacheGet(key: string): { durationS: number; distanceM: number } | null {
  const hit = cache.get(key);
  if (!hit) return null;
  if (Date.now() - hit.ts > TTL_MS) {
    cache.delete(key);
    return null;
  }
  // Refresh LRU order
  cache.delete(key);
  cache.set(key, hit);
  return { durationS: hit.durationS, distanceM: hit.distanceM };
}

function cacheSet(key: string, durationS: number, distanceM: number) {
  cache.set(key, { durationS, distanceM, ts: Date.now() });
  // Trim oldest entries past the cap.
  if (cache.size > MAX_ENTRIES) {
    const overflow = cache.size - MAX_ENTRIES;
    const it = cache.keys();
    for (let i = 0; i < overflow; i++) {
      const k = it.next().value;
      if (k) cache.delete(k);
    }
  }
}

/**
 * Fetch real road-network durations + distances from a single origin to N
 * destinations using the Mapbox Matrix API. Returns arrays aligned to the
 * destinations input. Entries may be null when the matrix has no answer.
 *
 * Mapbox cap is 25 coordinates per request (origin + 24 destinations), so
 * we batch beyond that.
 */
export async function fetchEtaMatrix(
  origin: LngLat,
  destinations: LngLat[],
  mode: TravelMode,
): Promise<{ durations: (number | null)[]; distances: (number | null)[] }> {
  const out: { durations: (number | null)[]; distances: (number | null)[] } = {
    durations: new Array(destinations.length).fill(null),
    distances: new Array(destinations.length).fill(null),
  };
  if (destinations.length === 0) return out;

  // Resolve from cache first; only fetch the destinations we don't have.
  const missingIdx: number[] = [];
  const missingDest: LngLat[] = [];
  for (let i = 0; i < destinations.length; i++) {
    const cached = cacheGet(cacheKey(origin, destinations[i], mode));
    if (cached) {
      out.durations[i] = cached.durationS;
      out.distances[i] = cached.distanceM;
    } else {
      missingIdx.push(i);
      missingDest.push(destinations[i]);
    }
  }
  if (!MAPBOX_TOKEN || missingDest.length === 0) return out;

  const profile = `mapbox/${MODE_PROFILE[mode]}`;
  const BATCH = 24; // 24 destinations + 1 origin = 25 coord cap

  for (let i = 0; i < missingDest.length; i += BATCH) {
    const slice = missingDest.slice(i, i + BATCH);
    const sliceIdx = missingIdx.slice(i, i + BATCH);
    const coords = [origin, ...slice].map((p) => `${p[0]},${p[1]}`).join(";");
    const url =
      `https://api.mapbox.com/directions-matrix/v1/${profile}/${coords}` +
      `?sources=0&annotations=duration,distance&access_token=${MAPBOX_TOKEN}`;
    try {
      const res = await fetch(url);
      if (!res.ok) continue;
      const data = await res.json();
      // sources=0 -> durations is [[d0, d1, ..., dN]] where d0 is origin->origin (0)
      const dur: (number | null)[] = data?.durations?.[0] ?? [];
      const dis: (number | null)[] = data?.distances?.[0] ?? [];
      for (let j = 0; j < slice.length; j++) {
        const d = dur[j + 1];
        const m = dis[j + 1];
        const outIdx = sliceIdx[j];
        if (typeof d === "number" && typeof m === "number") {
          out.durations[outIdx] = d;
          out.distances[outIdx] = m;
          cacheSet(cacheKey(origin, slice[j], mode), d, m);
        }
      }
    } catch {
      // leave nulls on failure; UI will hide the ETA badge
    }
  }
  return out;
}

export type ModeEta = { durationS: number; distanceM: number };
export type AllModeEtas = Partial<Record<TravelMode, ModeEta>>;

/** Convenience: fetch durations+distances for ALL three travel modes in
 *  parallel and pivot the result so each destination has a per-mode entry. */
export async function fetchAllModeEtaMatrix(
  origin: LngLat,
  destinations: LngLat[],
): Promise<AllModeEtas[]> {
  const modes: TravelMode[] = ["driving", "walking", "biking"];
  const results = await Promise.all(modes.map((m) => fetchEtaMatrix(origin, destinations, m)));
  return destinations.map((_, i) => {
    const entry: AllModeEtas = {};
    modes.forEach((m, mi) => {
      const d = results[mi].durations[i];
      const dm = results[mi].distances[i];
      if (typeof d === "number" && typeof dm === "number") {
        entry[m] = { durationS: d, distanceM: dm };
      }
    });
    return entry;
  });
}
